import { useEffect, useState } from "react";
import { LifeBuoy, X, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const PHONE = "844-305-1247";
const EMPTY = { name: "", email: "", product: "", subject: "", message: "" };

export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("support") === "open") {
      setOpen(true);
      params.delete("support");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  useEffect(() => {
    if (open && products.length === 0) {
      api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    }
  }, [open, products.length]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit =
    form.name.trim() && emailValid && form.product && form.subject.trim() && form.message.trim();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/support/tickets", {
        name: form.name.trim(),
        email: form.email.trim(),
        product: form.product,
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm(EMPTY); }, 300);
  };

  const inputCls =
    "w-full bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-3 py-2.5 transition-colors placeholder:text-slate-600";

  return (
    <>
      <button
        data-testid="support-badge"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[90] inline-flex items-center gap-2 bg-emerald-500 text-[#050505] hover:bg-emerald-400 transition-colors font-semibold px-5 py-3 rounded-full shadow-lg shadow-emerald-500/20"
      >
        <LifeBuoy size={18} /> Support
      </button>

      {open && (
        <div
          data-testid="support-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={close}
        >
          <div
            className="relative w-full max-w-lg bg-[#0B0B0B] border border-emerald-500/30 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="support-close" onClick={close} className="absolute top-3 right-3 text-slate-400 hover:text-white z-10">
              <X size={22} />
            </button>

            {done ? (
              <div className="p-10 text-center" data-testid="support-success">
                <CheckCircle2 size={52} strokeWidth={1.2} className="text-emerald-500 mx-auto mb-5" />
                <h2 className="font-display text-3xl text-slate-100">Ticket Received</h2>
                <p className="text-slate-400 text-sm mt-4">
                  Your support ticket has been received. We'll respond to your email shortly.
                </p>
                <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm mt-6">
                  <Phone size={15} /> Need us now? Call 24/7: {PHONE}
                </a>
                <button onClick={close} className="mt-8 w-full bg-white/10 text-slate-100 hover:bg-white/20 transition-colors py-3 text-sm">Close</button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-7 sm:p-8">
                <p className="label-caps text-emerald-400 mb-1 flex items-center gap-2">
                  <LifeBuoy size={14} /> Customer Support
                </p>
                <h2 className="font-display text-3xl text-slate-100">How can we help?</h2>

                <a
                  href={`tel:${PHONE}`}
                  data-testid="support-phone"
                  className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-3 py-2 text-xs mt-4"
                >
                  <Phone size={13} /> Customer Support available 24/7: {PHONE}
                </a>

                <div className="space-y-3 mt-5">
                  <input data-testid="support-name" placeholder="Your name" className={inputCls} value={form.name} onChange={set("name")} />
                  <div>
                    <input data-testid="support-email" type="email" placeholder="Email address" className={inputCls} value={form.email} onChange={set("email")} />
                    {form.email && !emailValid && <p className="text-xs text-red-400 mt-1">Enter a valid email address.</p>}
                  </div>
                  <select data-testid="support-product" className={`${inputCls} ${form.product ? "" : "text-slate-500"}`} value={form.product} onChange={set("product")}>
                    <option value="">Select a product…</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.name} className="text-slate-200">{p.name}</option>
                    ))}
                  </select>
                  <input data-testid="support-subject" placeholder="Subject" className={inputCls} value={form.subject} onChange={set("subject")} />
                  <textarea data-testid="support-message" rows={4} placeholder="Your message" className={inputCls} value={form.message} onChange={set("message")} />
                </div>

                <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                  Support tickets are for questions about our listed products. For all other inquiries,
                  call us 24/7 at <a href={`tel:${PHONE}`} className="text-emerald-400">{PHONE}</a>.
                </p>

                <button
                  type="submit"
                  data-testid="support-submit"
                  disabled={!canSubmit || submitting}
                  className="mt-5 w-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors font-medium py-3.5 text-sm tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting…" : "Submit Ticket"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
